/**
 * The hand-over from the landing page to the login form.
 *
 * Janeks: *"the webpage body moves to the left and fades"*, while the camera
 * pans right onto open ground and the form arrives over it.
 *
 * ─── Why the navigation waits ───────────────────────────────────────────────
 * `main.ts` swaps the contents of `#app` synchronously. If the Play button
 * navigated straight away, the landing page would be gone on the next frame
 * and there would be nothing left to slide out — the pan would happen behind a
 * login form that had already appeared. So the click starts the move, and the
 * navigation happens when it is over.
 *
 * ─── And why it cannot wait on the camera ──────────────────────────────────
 * The obvious wiring — navigate when `backdropFraming()` reaches 1 — makes the
 * login screen's arrival depend on a WebGL frame loop, which is not something
 * a navigation may depend on. A backgrounded tab, a lost context or a machine
 * with no GPU would leave the visitor on a faded-out landing page for ever.
 * The DOM transition is the clock; the camera is told where to go and travels
 * on its own, arriving at about the same time because the two durations are
 * tuned to match.
 */
import { setBackdropFraming, backdropIsLive } from './landing-backdrop';

/** Must match the `.lp-leaving` transition in `landing-styles.ts`. */
const LEAVE_MS = 620;

/** The class that slides and fades the page out. */
const LEAVING = 'lp-leaving';

let pending: number | null = null;

/**
 * Slides the page out, pans the backdrop, then navigates.
 *
 * Falls back to navigating immediately when there is no live backdrop — on a
 * phone or under reduced motion there is no camera to pan and no reason to
 * make someone wait 620 ms to see a form.
 */
export function transitionToAuth(page: HTMLElement, to: string, navigate: (to: string) => void): void {
  setBackdropFraming(1);
  if (!backdropIsLive() || prefersReducedMotion()) {
    navigate(to);
    return;
  }
  // A second click during the move must not queue a second navigation.
  if (pending !== null) return;
  page.classList.add(LEAVING);
  pending = window.setTimeout(() => {
    pending = null;
    navigate(to);
  }, LEAVE_MS);
}

/**
 * Cancels a move in flight — the page is being torn down for some other
 * reason (the back button, a link elsewhere) and must not navigate afterwards.
 */
export function cancelAuthTransition(): void {
  if (pending === null) return;
  window.clearTimeout(pending);
  pending = null;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
